import { Grid, Typography } from "@mui/material";
import { IUserAppSettings } from "@vmosolutions/oc-interfaces";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { updateUserAppSettings } from "src/redux/actions/App.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IBlockFormatPreferenceProps {
  id: string;
}

const BlockFormatPreference = forwardRef((props: IBlockFormatPreferenceProps, ref: any): JSX.Element => {
  const dispatch = useDispatch();
  const userAppSettings: IUserAppSettings = useSelector((state: IRootState) => state.authentication.appSettings);
  const { blockInfo } = userAppSettings;
  const [isFlightDisplayUTC, setIsFlightDisplayUTC] = useState<boolean>(blockInfo.utc);
  const [isFlightDisplayICAO, setIsFlightDisplayICAO] = useState<boolean>(blockInfo.icao);

  useEffect(() => {
    setIsFlightDisplayICAO(blockInfo.icao);
    setIsFlightDisplayUTC(blockInfo.utc);
  }, [blockInfo]);

  //NOTE: No auto saving in this version currently as all values will expect a "Save" button click at form bottom. Rework if this changes
  const toggleICAO = useCallback(
    (value: boolean): void => {
      setIsFlightDisplayICAO(value);
      dispatch(
        updateUserAppSettings(
          { ...userAppSettings, blockInfo: { ...userAppSettings.blockInfo, ...{ icao: value } } },
          "blockInfo"
        )
      );
    },
    [dispatch, userAppSettings]
  );

  const saveAsMyDefault = useCallback(async () => {
    await AmplifyAPIWrapper.Put<any>("/me/settings", {
      blockInfo: {
        icao: isFlightDisplayICAO,
        utc: isFlightDisplayUTC,
      },
    });
  }, [isFlightDisplayICAO, isFlightDisplayUTC]);

  const toggleUTC = useCallback(
    (value: boolean): void => {
      setIsFlightDisplayUTC(value);
      dispatch(
        updateUserAppSettings(
          { ...userAppSettings, blockInfo: { ...userAppSettings.blockInfo, ...{ utc: value } } },
          "blockInfo"
        )
      );
    },
    [dispatch, userAppSettings]
  );

  useImperativeHandle(ref, () => ({
    saveDefault() {
      return {
        icao: isFlightDisplayICAO,
        utc: isFlightDisplayUTC,
      };
    },
  }));

  return (
    <div className="switch_fields">
      <Grid container alignItems={"center"}>
        <Grid item>
          <Typography variant="body2">Time Display Format</Typography>
        </Grid>
        <Grid item>
          <SwitchField
            className={`display_utc toggle_label ${!isFlightDisplayUTC && "active"}`}
            name="UTC_Local"
            value={isFlightDisplayUTC}
            onChange={toggleUTC}
            switchlabels={["UTC", "LOCAL"]}
            switchtooltips={["Times as UTC", "Times as local"]}
            label=""
            allowNull={false}
          />
        </Grid>
        <Grid item style={{ width: 200 }}>
          <Typography variant="body2" textAlign={"right"}>
            Flight Display Format
          </Typography>
        </Grid>
        <Grid item>
          <SwitchField
            className={`display_icao toggle_label ${!isFlightDisplayICAO && "active"}`}
            name="IATA_ICAO"
            value={isFlightDisplayICAO}
            onChange={toggleICAO}
            switchlabels={["ICAO", "IATA"]}
            switchtooltips={["Locations as ICAO", "Locations as IATA"]}
            label=""
            allowNull={false}
          />
        </Grid>
      </Grid>
    </div>
  );
});

export { BlockFormatPreference };
